import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Subscription,
  catchError,
  distinctUntilChanged,
  filter,
  of,
  pairwise,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  DELIBERATE_LOGOUT_KEY,
  DELIBERATE_LOGOUT_TTL_MS,
  DeliberateLogoutFlag,
} from '@org/shared';
import { OrgContextService } from './org-context.service';

/**
 * State of the user's authenticated session as the SPA sees it.
 *
 * `active`   — token valid, silent refresh working, no user intervention needed.
 * `expired`  — silent refresh failed or a 401 came back from the API. The
 *              expired-session dialog is shown; the interceptor swallows any
 *              further 401s so downstream feature toasts don't fire.
 */
export type SessionState = 'active' | 'expired';

/**
 * Minutes before an access token's `exp` claim that the proactive watcher
 * schedules a silent refresh. Auth0's own recommendation is "well before
 * expiry" — a couple of minutes is generous without being wasteful.
 */
const REFRESH_LEAD_MINUTES = 2;

// DELIBERATE_LOGOUT_KEY is imported from @org/shared so both this
// service and the header (which cannot import from apps/) resolve to
// the same identifier — no risk of one side renaming and the other
// silently going stale. See libs/shared/src/lib/auth/session-constants.ts

/**
 * Cap on pendingSwallowedRequests. Only firstValueFrom callers produce
 * a matching EmptyError to decrement the counter; plain `.subscribe`
 * callers can inflate it without bound. Cap bounds the number of
 * unrelated EmptyErrors that could ever be accidentally silenced.
 */
const MAX_PENDING_SWALLOWED_REQUESTS = 32;

/**
 * Delay before promoting an unflagged `isAuthenticated$ = false` to
 * `markExpired`. Long enough for a `beforeunload` from a real logout
 * navigation to arrive and cancel it; short enough that a real expiry
 * still surfaces the dialog quickly. See scheduleDelayedExpiry() below.
 */
const DELAYED_EXPIRY_MS = 400;

/**
 * Absolute floor for the refresh timer. Zero — not a defensive positive
 * value — so an already-expired (or nearly-expired) token attempts refresh
 * on the very next event-loop tick rather than sitting for a full second
 * while any API call the app makes in that window sends the dead token
 * and triggers the reactive 401 path unnecessarily. setTimeout(fn, 0)
 * still yields to the current tick, so this is not a synchronous re-entry
 * into scheduleRefresh.
 */
const MIN_REFRESH_DELAY_MS = 0;

/**
 * Single source of truth for the SPA's session state. Two watchers feed it:
 *
 *   1. Proactive expiry watcher (inside this service) — decodes the access
 *      token, schedules a `getAccessTokenSilently()` a couple of minutes
 *      before the `exp` claim fires. Happy path — user never notices.
 *
 *   2. Reactive 401 interceptor (auth-expiry.interceptor.ts) — safety net for
 *      when silent refresh fails or a request slips through with a bad token.
 *      Calls `markExpired()` here rather than driving the redirect itself.
 *
 * Both funnel through `markExpired()`, which flips state to `expired`
 * (idempotent — multiple simultaneous 401s produce ONE dialog, ONE redirect).
 * The SessionExpiredDialog component reads `state()` and shows itself when
 * expired; its "Sign in again" button calls `reauthenticate()`.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly orgContext = inject(OrgContextService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _state = signal<SessionState>('active');
  readonly state = this._state.asReadonly();
  readonly isExpired = computed(() => this._state() === 'expired');

  /** Guard so the "sign in again" click can't fire two Auth0 redirects. */
  private reauthInFlight = false;

  /**
   * URL captured at markExpired() time so post-reauth navigation returns
   * the user to the page they were on WHEN THE SESSION DIED, not the
   * page they happen to be on when they click "Sign in again" — the
   * dialog can sit open for many seconds and background nav (router
   * preload, query-param updates from polling services) can silently
   * mutate router.url in the meantime.
   */
  private expiredAtUrl: string | null = null;

  /**
   * Timestamp of the most recent markExpired() call, used to scope the
   * EmptyError suppression window narrowly. See the unhandledrejection
   * listener in the constructor.
   */
  private expiredAtTs: number | null = null;

  /** Handle for the pending silent-refresh timer so we can cancel + reschedule
   *  when a fresh token arrives (rotation) or the user logs out. */
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  /** Live subscription for the in-flight `getAccessTokenSilently()` inside
   *  the refresh timer's callback. Tracked so markExpired() and DestroyRef
   *  teardown can cancel it — otherwise a late success would deliver a
   *  post-expiry (or post-destroy) callback into scheduleRefresh(). */
  private refreshRequest: Subscription | null = null;

  /** Pending delayed-markExpired timer — see scheduleDelayedExpiry(). */
  private delayedExpiryTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Counter of 401 responses the interceptor has swallowed into EMPTY
   * and is expecting to see surface as EmptyError through firstValueFrom
   * callers. Incremented by note401Swallowed() (called from the
   * interceptor) and decremented as the unhandledrejection listener
   * consumes matching rejections. Only rejections while this counter is
   * > 0 get suppressed — no more, no less. Ensures unrelated EmptyErrors
   * (e.g. a search that legitimately returns empty consumed with
   * firstValueFrom) surface normally even during the post-expiry window.
   */
  private pendingSwallowedRequests = 0;

  constructor() {
    // Every time the SDK exposes a new access token (initial login, silent
    // refresh, forced refresh) — decode its exp claim and schedule the next
    // silent refresh a few minutes before it fires.
    this.auth.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        filter(Boolean),
        switchMap(() =>
          // @auth0/auth0-angular's getAccessTokenSilently() already returns
          // an Observable<string>, so no `from()` wrapper is needed — that
          // was creating a misleading asymmetry with the direct call in
          // scheduleRefresh() below.
          this.auth.getAccessTokenSilently().pipe(
            catchError(err => {
              // Boot-time silent-fetch failures are frequently transient
              // (Auth0 slow, network blip, first-load race with the SDK's
              // own bootstrap). Don't slam up the blocking dialog here —
              // just log and skip scheduling. The reactive 401 interceptor
              // will catch a REAL expiry the next time the app calls the
              // API, which is a more truthful signal than a boot-time
              // fetch failure.
              console.warn('[SessionService] boot-time getAccessTokenSilently failed (deferring to reactive path)', err);
              return of(null);
            }),
          ),
        ),
        filter((t): t is string => !!t),
        tap(token => this.scheduleRefresh(token)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Only react to genuine `true → false` transitions. Using pairwise
    // (with startWith(false) to prime the first pair) avoids the
    // BehaviorSubject's initial `false` during SDK bootstrap AND avoids
    // any brittle "have we ever been active" latch that could go stale
    // if an in-place re-auth flow (silent popup, markActive()) is added
    // later — pairwise reads the actual last-observed value, not a flag
    // that outlives its usefulness.
    this.auth.isAuthenticated$
      .pipe(
        startWith(false),
        pairwise(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([prev, curr]) => {
        // Only act on genuine `true → false` transitions. The boot pair
        // is `[false, false]` (startWith(false) followed by the
        // BehaviorSubject's initial false) — if we consumed the
        // deliberate-logout flag on THAT emission, a user who was mid
        // hard-reload during a blocked logout attempt would find the
        // flag drained before the SDK ever emitted true, and the real
        // expiry check would then wrongly show the dialog.
        if (prev && !curr) {
          // Gate BEFORE consuming — otherwise a stray `true → false`
          // arriving while state is already 'expired' (e.g. a silent
          // popup finishing during an existing expired cycle, or a
          // future in-place re-auth flow that flips false again) would
          // drain the flag without acting on it. The very next real
          // deliberate logout would then find no flag and get treated
          // as expiry, showing the dialog to a user who just signed out.
          if (this._state() !== 'active') return;
          const wasDeliberate = this.consumeDeliberateLogoutFlag();

          if (wasDeliberate) {
            // User-initiated logout — SDK is about to redirect the
            // browser away; SPA will unload. Tear down the refresh
            // timer AND cancel any in-flight silent fetch so a
            // late-arriving success can't re-arm the chain against
            // a session that's being intentionally torn down.
            this.clearRefreshTimer();
            this.cancelInFlightRefresh();
            return;
          }

          // Defensive: some code path may call auth.logout() without
          // first setting the deliberate-logout flag (sessionStorage
          // unavailable, a future caller that forgets, extension
          // interfering with storage). Rather than immediately slamming
          // up the expired dialog, defer markExpired() by a short delay
          // and cancel it if a page unload starts in the meantime — a
          // genuine logout navigates within milliseconds, a real expiry
          // keeps the SPA alive. The reactive 401 interceptor's own
          // markExpired() call is unaffected (it's synchronous on API
          // response and doesn't route through this handler).
          this.scheduleDelayedExpiry();
        }
      });

    // The 401 interceptor returns EMPTY (not NEVER) so `finalize` /
    // `takeUntil` / loading-flag cleanups run on expiry. Side effect:
    // any call-site using `firstValueFrom(http$)` on a request that
    // gets 401'd now rejects with EmptyError — which would surface as
    // an unhandled promise rejection even though the failure is a
    // routine session expiry the dialog is already handling.
    //
    // Suppress that noise using a MATCHED counter, not a time window:
    // the interceptor calls note401Swallowed() every time it returns
    // EMPTY, incrementing pendingSwallowedRequests. The listener
    // decrements as it consumes matching rejections, and only
    // suppresses while the counter is positive. As a belt-and-braces
    // measure the counter is also gated to the short post-expiry
    // window so a stray increment can't linger indefinitely.
    // Unrelated EmptyErrors (a search legitimately returning empty
    // consumed with firstValueFrom) always surface — even during the
    // window — because the counter tracks OUR specific rejections.
    if (typeof window !== 'undefined') {
      const listener = (e: PromiseRejectionEvent) => {
        // Only suppress while (a) session is expired AND (b) the counter
        // says we're still expecting to swallow at least one of our own
        // 401-derived EmptyErrors. Removes the older 3s wall-clock
        // window — late 401s can (and do) trickle in after that, and
        // their EmptyErrors would otherwise surface as unhandled
        // rejections for the rest of the expired session. Counter is
        // bounded by markActive()'s reset and by the state guard
        // (`expired` state can only end via markActive or SPA unload).
        if (this._state() !== 'expired') return;
        if (this.pendingSwallowedRequests <= 0) return;
        const reason = e.reason as { name?: string } | null | undefined;
        if (reason?.name === 'EmptyError') {
          // Floor at 0 — belt-and-braces against any ordering weirdness
          // between the interceptor's note401Swallowed() and the
          // synchronous EmptyError propagation. A negative counter
          // would silently keep suppression open indefinitely.
          this.pendingSwallowedRequests = Math.max(0, this.pendingSwallowedRequests - 1);
          e.preventDefault();
        }
      };
      window.addEventListener('unhandledrejection', listener);
      this.destroyRef.onDestroy(() => window.removeEventListener('unhandledrejection', listener));
    }

    // pagehide (not beforeunload) fires only when the page is actually
    // being torn down. beforeunload fires for prompt-cancellable
    // navigations too — a "Leave site?" that the user aborts would
    // still cancel our timer and silently suppress a real expiry.
    // pagehide is also more reliable on mobile Safari.
    //
    // pageshow with persisted=true fires when the page is restored
    // from bfcache (Back/Forward Cache). While the page was frozen,
    // isAuthenticated$ didn't emit — so a token that expired during
    // that sleep won't trip the pairwise handler on restore. Kick
    // the proactive refresh chain by asking for a fresh token; on
    // success we schedule the next refresh; on failure we defer
    // to the reactive 401 path (same as boot-time).
    if (typeof window !== 'undefined') {
      const onPageHide = () => {
        this.cancelDelayedExpiry();
        // Also tear down the refresh timer + in-flight fetch so a
        // paused timer resuming after bfcache restore can't race with
        // onPageShow's re-arm. onPageShow re-arms fresh state anyway.
        this.clearRefreshTimer();
        this.cancelInFlightRefresh();
      };
      const onPageShow = (e: PageTransitionEvent) => {
        if (!e.persisted) return;
        // If we're already showing the expired dialog, don't kick a
        // fresh token fetch — the user will re-auth via the button,
        // and a parallel /token round-trip here would burn quota and
        // muddy the console with a request that races the dialog.
        if (this._state() === 'expired') return;
        console.info('[SessionService] Restored from bfcache — re-arming refresh chain');
        // Clear BOTH the pending setTimeout AND the in-flight
        // subscription. The pre-freeze refresh timer's setTimeout
        // handle survives bfcache — if we don't cancel it, it can
        // fire moments after our re-arm and issue a parallel
        // getAccessTokenSilently that races the one we're about to
        // kick off here.
        this.clearRefreshTimer();
        this.cancelInFlightRefresh();
        this.refreshRequest = this.auth
          .getAccessTokenSilently({ cacheMode: 'off' })
          .pipe(take(1))
          .subscribe({
            next: fresh => {
              this.refreshRequest = null;
              this.scheduleRefresh(fresh);
            },
            error: () => {
              this.refreshRequest = null;
              // Don't slam up the dialog on bfcache-restore fetch
              // failures — reactive 401 catches a real expiry.
            },
          });
      };
      window.addEventListener('pagehide', onPageHide);
      window.addEventListener('pageshow', onPageShow);
      this.destroyRef.onDestroy(() => {
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('pageshow', onPageShow);
      });
    }

    this.destroyRef.onDestroy(() => {
      this.clearRefreshTimer();
      this.cancelInFlightRefresh();
      this.cancelDelayedExpiry();
    });
  }

  /**
   * Flip state to `expired`. Idempotent — the second and subsequent callers
   * (e.g. a burst of parallel 401s) return without doing anything, so exactly
   * one dialog appears and exactly one redirect is queued when the user acts.
   *
   * `reason` is logged for debugging only; not shown to the user.
   */
  markExpired(reason: string): void {
    if (this._state() === 'expired') return;
    console.warn('[SessionService] Session expired:', reason);
    // Snapshot the URL AT expiry — reauthenticate() reads this rather
    // than sampling router.url later, so background navigation during
    // the redirect window can't corrupt the return target.
    this.expiredAtUrl = this.router.url;
    this.expiredAtTs = Date.now();
    // Deliberately do NOT reset pendingSwallowedRequests here — the
    // interceptor calls note401Swallowed() alongside markExpired() and
    // the two can run in either order. Resetting here would zero out
    // an increment made just beforehand. The counter is bounded by
    // (a) the 3s window that resets it to 0 in the listener, and
    // (b) markActive() as the state-transition reset point.
    this.clearRefreshTimer();
    this.cancelInFlightRefresh();
    this.cancelDelayedExpiry();
    this._state.set('expired');
    // Auto-redirect — the dialog previously required an explicit
    // "Sign in again" click, which users expected the UI to do for
    // them. The brief dialog that renders is now purely a visual
    // "redirecting…" indicator; reauthenticate() navigates away
    // within the next event-loop tick on the happy path. A tiny
    // setTimeout lets Angular commit the state=expired signal change
    // first (so the dialog's "Redirecting…" copy flashes briefly
    // before the SPA unloads) and lets the current synchronous call
    // stack unwind cleanly before Auth0 blows the page away.
    setTimeout(() => this.reauthenticate(), 0);
  }

  /**
   * Called by the 401 interceptor every time it swallows a response
   * into EMPTY, so the unhandledrejection listener can suppress the
   * matching EmptyError that firstValueFrom callers will emit — no
   * more, no less. See the listener in the constructor.
   */
  note401Swallowed(): void {
    // Order-independent with markExpired(): if the interceptor calls
    // note401Swallowed FIRST (either intentionally or via a caller
    // reordering), expiredAtTs is still null — seed it now.
    if (this.expiredAtTs === null) {
      this.expiredAtTs = Date.now();
    }
    // Cap the counter. Only firstValueFrom / lastValueFrom callers
    // produce a matching EmptyError to decrement; plain `.subscribe`
    // callers see clean completion and never decrement. Background
    // pollers firing 401s across a long expired window would otherwise
    // inflate the counter without bound, so once counter reaches the
    // cap we stop incrementing. That bounds the WORST-CASE number of
    // unrelated EmptyErrors we could accidentally silence to
    // MAX_PENDING_SWALLOWED_REQUESTS, which is small enough not to
    // meaningfully mask real bugs.
    if (this.pendingSwallowedRequests >= MAX_PENDING_SWALLOWED_REQUESTS) return;
    this.pendingSwallowedRequests++;
  }

  private scheduleDelayedExpiry(): void {
    this.cancelDelayedExpiry();
    this.delayedExpiryTimer = setTimeout(() => {
      this.delayedExpiryTimer = null;
      // Re-check state — a concurrent 401 could have already fired
      // markExpired synchronously.
      if (this._state() === 'active') {
        this.markExpired('sdk-state-false');
      }
    }, DELAYED_EXPIRY_MS);
  }

  private cancelDelayedExpiry(): void {
    if (this.delayedExpiryTimer !== null) {
      clearTimeout(this.delayedExpiryTimer);
      this.delayedExpiryTimer = null;
    }
  }

  /**
   * Reset state back to `active` after a successful re-authentication —
   * intended for in-place re-auth flows (silent popup, backchannel token
   * refresh that succeeds after a prior failure) where the SPA is NOT
   * unloaded. In the current design every reauthenticate() drives an
   * Auth0 redirect that unloads the SPA, so this is never called on
   * that path — but leaving the state a one-way latch would silently
   * strand the dialog if we ever add such a flow later, and this method
   * makes the intended transition explicit for tests too.
   */
  markActive(): void {
    if (this._state() === 'active') return;
    console.info('[SessionService] Session restored to active');
    // Cancel a pending delayed-expiry timer — otherwise a callsite
    // that restored the session in-place (silent popup finishing after
    // a stray isAuthenticated$ = false) would see the dialog reappear
    // 400 ms later when that stale timer fires.
    this.cancelDelayedExpiry();
    // Also tear down any pre-expiry refresh timer + in-flight fetch.
    // markExpired cleared these when it fired, but if markActive is
    // reached via a path that never went through markExpired (e.g.
    // test harness) an orphaned timer would fire and start a parallel
    // refresh chain against a session that was just restored.
    this.clearRefreshTimer();
    this.cancelInFlightRefresh();
    // Clear the expiry-time snapshots so a subsequent expiry captures
    // fresh state rather than inheriting the previous one.
    this.expiredAtUrl = null;
    this.expiredAtTs = null;
    this.pendingSwallowedRequests = 0;
    this._state.set('active');
    // Re-arm the proactive refresh chain. If an in-place re-auth
    // (silent popup, test harness) called markActive without going
    // through isAuthenticated$ false→true (which is what normally
    // wakes the boot pipeline), scheduleRefresh would never re-run
    // and the chain would stay dead until the next real 401.
    this.refreshRequest = this.auth
      .getAccessTokenSilently({ cacheMode: 'off' })
      .pipe(take(1))
      .subscribe({
        next: fresh => {
          this.refreshRequest = null;
          this.scheduleRefresh(fresh);
        },
        error: () => {
          this.refreshRequest = null;
          // No token available in-place — reactive path will catch
          // the next 401.
        },
      });
  }

  /**
   * Called by UI that initiates a deliberate sign-out (e.g. header's
   * "Log out" menu item) BEFORE it calls `auth.logout()`. Sets the
   * sessionStorage flag the isAuthenticated$ watcher checks, so the
   * about-to-fire false transition doesn't get mistaken for expiry
   * and doesn't show the expired dialog to a user who just chose to
   * sign out.
   *
   * The header currently calls auth.logout() directly — call this
   * helper (or set the flag manually with the exported KEY) alongside
   * it. Kept as an idempotent no-op if the flag is somehow already set.
   */
  beginDeliberateLogout(): void {
    try {
      const flag: DeliberateLogoutFlag = { ts: Date.now() };
      sessionStorage.setItem(DELIBERATE_LOGOUT_KEY, JSON.stringify(flag));
    } catch {
      // sessionStorage may be unavailable (privacy modes, embedded contexts).
      // Worst case: user sees the expired dialog momentarily before the
      // logout redirect unloads the SPA — degrades gracefully.
    }
  }

  /**
   * Read-and-clear the deliberate-logout flag atomically, honoring the
   * TTL. Returns true only if the flag is present AND fresh (younger
   * than DELIBERATE_LOGOUT_TTL_MS). A stale flag — left over from a
   * blocked logout redirect that never actually navigated — is cleared
   * and treated as absent, so the NEXT real expiry surfaces the dialog
   * instead of being silently swallowed.
   *
   * Consume semantics: reading always clears, whether fresh or stale.
   */
  private consumeDeliberateLogoutFlag(): boolean {
    try {
      const raw = sessionStorage.getItem(DELIBERATE_LOGOUT_KEY);
      if (raw === null) return false;
      // Clear immediately — regardless of freshness, we don't want a
      // stale entry to survive a later read.
      sessionStorage.removeItem(DELIBERATE_LOGOUT_KEY);

      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        // Legacy `'1'` (pre-TTL) or corrupt value — treat as stale
        // and require the user to log out again for the intent to
        // count. Safe default.
        return false;
      }
      // Explicit ts-is-number check. Without it, `Date.now() - undefined`
      // is NaN, which fails BOTH inequality guards below (NaN < 0 is
      // false, NaN > TTL is false), silently returning true and
      // permanently suppressing the expired dialog on the next real
      // expiry. Any object missing a numeric ts is treated as stale.
      const ts = (parsed as DeliberateLogoutFlag | null)?.ts;
      if (typeof ts !== 'number' || !Number.isFinite(ts)) {
        console.info('[SessionService] Ignoring deliberate-logout flag with invalid ts');
        return false;
      }

      const age = Date.now() - ts;
      if (age < 0 || age > DELIBERATE_LOGOUT_TTL_MS) {
        console.info('[SessionService] Ignoring stale deliberate-logout flag (age', age, 'ms)');
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * User clicked "Sign in again" on the expired dialog. Kicks off the Auth0
   * redirect with `prompt: 'login'` so the user always sees the login form
   * (any stale IdP cookie is ignored), and preserves the current URL so they
   * land back where they were after re-authenticating.
   */
  reauthenticate(): void {
    if (this.reauthInFlight) return;
    this.reauthInFlight = true;

    // Safety net for the happy path: loginWithRedirect() completes once
    // the redirect has been INITIATED, not once navigation has succeeded.
    // If the browser doesn't navigate (blocked popup, extension suppressing
    // the redirect, network stall on the /authorize call the SDK issues
    // first), no error fires and without this timer the guard would stay
    // `true` forever, making every subsequent "Sign in again" click a
    // permanent no-op.
    //
    // `navigationInitiated` is set from each subscribe's complete handler.
    // On the real happy path the browser navigates away and the timer
    // never gets to run — the JS context dies with the page. If the timer
    // DOES fire, we only warn+release when navigation was NEVER initiated
    // (an actual stall) rather than after a completion that was followed
    // by a slow-but-successful navigation.
    let navigationInitiated = false;
    const releaseGuardTimer = setTimeout(() => {
      if (!this.reauthInFlight) return;
      if (!navigationInitiated) {
        console.warn('[SessionService] loginWithRedirect stalled — releasing guard');
      }
      this.reauthInFlight = false;
    }, 10_000);

    const releaseGuard = (reason: string, err?: unknown) => {
      clearTimeout(releaseGuardTimer);
      this.reauthInFlight = false;
      console.error(`[SessionService] ${reason}`, err ?? '');
    };

    // Prefer the URL captured at expiry time; fall back to current URL
    // only if reauthenticate() was somehow called without markExpired()
    // running first (defensive — not expected in the normal flow).
    const target = this.expiredAtUrl ?? this.router.url;
    // take(1) so a multi-emit or hot orgContext observable can't fire the
    // redirect (and subsequent nested subscribes) more than once.
    this.orgContext.resolve().pipe(take(1)).subscribe({
      next: ctx =>
        this.auth
          .loginWithRedirect({
            authorizationParams: {
              prompt: 'login',
              ...(ctx?.orgId ? { organization: ctx.orgId } : {}),
            },
            appState: { target },
          })
          .subscribe({
            complete: () => { navigationInitiated = true; },
            error: err => releaseGuard('loginWithRedirect failed', err),
          }),
      error: err => {
        // Org lookup failed — fall back to a redirect without an org hint
        // rather than leaving the user staring at a locked dialog.
        console.error('[SessionService] orgContext.resolve() failed, falling back', err);
        this.auth
          .loginWithRedirect({
            authorizationParams: { prompt: 'login' },
            appState: { target },
          })
          .subscribe({
            // Symmetrical with the primary path — the outer 10s safety
            // timer covers a stalled complete-without-navigation scenario
            // for both paths.
            complete: () => { navigationInitiated = true; },
            error: e => releaseGuard('fallback loginWithRedirect failed', e),
          });
      },
    });
  }

  private scheduleRefresh(token: string): void {
    // ⚠️ LOAD-BEARING GUARD — do NOT remove without reading this comment.
    //
    // The safety of the concurrent flow between the proactive refresh
    // timer and reactive markExpired() rests on this single check.
    // Timeline:
    //   t=0    Proactive timer fires getAccessTokenSilently(cacheMode:off)
    //   t=1    Reactive 401 arrives via interceptor → markExpired() runs,
    //          which calls cancelInFlightRefresh() to unsubscribe the
    //          request from step 0.
    //   t=2    But if the SDK's request had already resolved by the time
    //          unsubscribe was called (racy — very small window), the
    //          `next` callback still fires with a fresh token and calls
    //          scheduleRefresh() re-entrantly. Without this guard, the
    //          timer chain re-arms behind the visible expired dialog.
    //
    // Similarly for the boot pipeline: a silent-fetch success arriving
    // after a same-tick markExpired() would restart the chain here.
    //
    // The second guard (inside the setTimeout callback below) covers the
    // reverse race — markExpired() fires AFTER the timer is scheduled
    // but BEFORE its callback runs. Both guards need to stay.
    if (this._state() === 'expired') return;

    this.clearRefreshTimer();
    const expMs = this.decodeExpMs(token);
    if (!expMs) return; // Not a JWT we can read — rely on reactive path.

    const leadMs = REFRESH_LEAD_MINUTES * 60 * 1000;
    const delay = Math.max(MIN_REFRESH_DELAY_MS, expMs - Date.now() - leadMs);

    this.refreshTimer = setTimeout(() => {
      // Clear our own handle as the very first thing in the callback so
      // the invariant "refreshTimer non-null ↔ timer is pending" stays
      // true. Otherwise concurrent code calling clearRefreshTimer()
      // between fire and re-arm sees a stale handle, nulls it, and a
      // later scheduleRefresh arms a second timer without cancelling
      // the in-flight fetch this callback is about to start.
      this.refreshTimer = null;

      // Re-check state at fire time — the session may have expired via the
      // reactive 401 path between scheduling and firing.
      if (this._state() === 'expired') return;

      // cacheMode: 'off' forces a network refresh rather than returning the
      // still-cached (but soon-to-expire) token.
      //
      // We deliberately re-schedule from HERE using the fresh token rather
      // than relying on `isAuthenticated$` to re-emit: that BehaviorSubject
      // only fires on false↔true transitions, so silent rotation (which
      // keeps the state true) never re-emits. Without this self-restart
      // the timer chain would fire exactly once and then die silently.
      //
      // Track the subscription in refreshRequest so it can be cancelled by
      // markExpired() and DestroyRef teardown — otherwise a late-arriving
      // success (or Auth0 SDK's retry logic) could deliver a callback into
      // scheduleRefresh() after the service has already been torn down.
      this.cancelInFlightRefresh();
      this.refreshRequest = this.auth
        .getAccessTokenSilently({ cacheMode: 'off' })
        // take(1) so a hot / multi-emit observable can't call
        // scheduleRefresh() more than once per timer fire — otherwise
        // back-to-back emissions would arm multiple concurrent refresh
        // timers, each racing to hit the token endpoint.
        .pipe(take(1))
        .subscribe({
          next: fresh => {
            this.refreshRequest = null;
            this.scheduleRefresh(fresh);
          },
          error: () => {
            this.refreshRequest = null;
            this.markExpired('silent-refresh-failed');
          },
        });
    }, delay);
  }

  private cancelInFlightRefresh(): void {
    if (this.refreshRequest) {
      this.refreshRequest.unsubscribe();
      this.refreshRequest = null;
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Decode a JWT's `exp` claim (seconds since epoch → ms). Returns null if
   * the token isn't a decodable JWT (opaque access tokens from some APIs).
   * Deliberately does NOT verify the signature — that's the API's job; we
   * just want the timing hint.
   */
  private decodeExpMs(token: string): number | null {
    const parts = token.split('.');
    if (parts.length !== 3) {
      // Opaque access token (some Auth0 API configurations issue these).
      // Not an error — just tells us we can't decode `exp` locally.
      console.info('[SessionService] Access token is opaque (non-JWT); proactive refresh disabled — falling back to reactive 401 path.');
      return null;
    }
    if (!parts[1]) {
      // Well-formed dots but empty payload — malformed JWT. Log
      // explicitly rather than silently returning null, otherwise it's
      // indistinguishable from the opaque-token case above.
      console.warn('[SessionService] JWT has empty payload segment; cannot decode exp.');
      return null;
    }
    try {
      // JWT payloads are base64url (no padding, `-`/`_` instead of `+`/`/`).
      // atob() requires standard base64 WITH `=` padding — so we translate
      // the alphabet AND re-pad to a multiple of 4. Without the padEnd,
      // any token whose payload length isn't already divisible by 4
      // (i.e. most real-world tokens) throws InvalidCharacterError and
      // the proactive refresh chain silently disables itself.
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
      const payload = JSON.parse(atob(padded));
      if (typeof payload.exp !== 'number') {
        console.warn('[SessionService] JWT payload has no numeric exp claim; cannot schedule refresh.');
        return null;
      }
      return payload.exp * 1000;
    } catch (err) {
      console.warn('[SessionService] Failed to decode JWT payload for exp claim', err);
      return null;
    }
  }
}
